export const signatureCode = {
  SIMPLE: 'SIMPLE',
  SMS: 'SMS',
  ASSECO: 'ASSECO',
  MSZAFIR_SHORT: 'MSZAFIR_SHORT',
  MSZAFIR_LONG: 'MSZAFIR_LONG',
  SIGNIUS: 'SIGNIUS',
  ZEALID: 'ZEALID',
  EXTERNAL: 'EXTERNAL',
} as const;

export type SignatureCode = (typeof signatureCode)[keyof typeof signatureCode];

export type Kind = 'document' | 'qualified';

export type SignatureDetails = {
  code: SignatureCode;
  kind: Kind;
  family?: string;
  massSigneLimit: number;
  qualification: 'QUALIFIED' | 'ADVANCED' | 'INDETERMINATE' | 'NOT_QUALIFIED';
};

/**
 * Catalog of signatures
 */
const signatureCatalog: SignatureDetails[] = [
  {
    code: signatureCode.SIMPLE,
    kind: 'document',
    qualification: 'NOT_QUALIFIED',
    massSigneLimit: 200,
  },
  {
    code: signatureCode.SMS,
    kind: 'document',
    qualification: 'NOT_QUALIFIED',
    massSigneLimit: 200,
  },
  {
    code: signatureCode.ASSECO,
    kind: 'qualified',
    qualification: 'QUALIFIED',
    massSigneLimit: 25,
  },
  {
    code: signatureCode.MSZAFIR_SHORT,
    kind: 'qualified',
    family: 'MSZAFIR',
    qualification: 'QUALIFIED',
    massSigneLimit: 1,
  },
  {
    code: signatureCode.MSZAFIR_LONG,
    kind: 'qualified',
    family: 'MSZAFIR',
    qualification: 'QUALIFIED',
    massSigneLimit: 20,
  },
  {
    code: signatureCode.SIGNIUS,
    kind: 'qualified',
    qualification: 'QUALIFIED',
    massSigneLimit: 20,
  },
  {
    code: signatureCode.ZEALID,
    kind: 'qualified',
    qualification: 'QUALIFIED',
    massSigneLimit: 10,
  },
  {
    code: signatureCode.EXTERNAL,
    kind: 'qualified',
    qualification: 'QUALIFIED',
    massSigneLimit: 1,
  },
];

/**
 * Get signature by code
 *
 * @param code
 */
const getSignatureByCode = (code: SignatureCode) =>
  signatureCatalog.find(signature => signature.code === code);

/**
 * Get the signatures that can be used to sign documents
 */
const getDocumentSignatures = () =>
  signatureCatalog.filter(signature => signature.kind === 'document');

/**
 * Get the document signe codes of the signatures that can be used to sign documents
 */
const getDocumentSignatureCodes = () =>
  getDocumentSignatures().map(signature => signature.code);

/**
 * Check if a signature can be used to sign documents
 *
 * @param code - signature code
 */
const isDocumentSignature = (code: SignatureCode) =>
  getDocumentSignatureCodes().includes(code);

/**
 * Get the signatures that can be used to sign documents
 */
const getQualifiedSignatures = () =>
  signatureCatalog.filter(signature => signature.kind === 'qualified');

/**
 * Get the qualified signe codes of the signatures that can be used to sign documents
 */
const getQualifiedSignatureCodes = () =>
  getQualifiedSignatures().map(signature => signature.code);

/**
 * Check if a signature can be used to sign documents
 *
 * @param code - signature code
 */
const isQualifiedSignature = (code: SignatureCode) =>
  getQualifiedSignatureCodes().includes(code);

type ScopeType = 'SMS' | 'ANY' | 'QUALIFIED' | 'HYBRID';

/**
 * Get signatures for a given signatures scope
 * @param scope
 */
const getSignaturesForScope = (scope: ScopeType | undefined) => {
  switch (scope) {
    case 'SMS':
      return signatureCatalog.filter(signature => signature.code === 'SMS');
    case 'QUALIFIED':
      return getQualifiedSignatures();
    case 'HYBRID':
      return [
        // biome-ignore lint/style/noNonNullAssertion: this code  should always be present
        getSignatureByCode(signatureCode.SMS)!,
        ...getQualifiedSignatures(),
      ];
    default:
      return signatureCatalog;
  }
};

/**
 * Get the signatures codes for a given signatures scope
 * @param scope
 */
const getSignaturesForScopeCodes = (scope: ScopeType) =>
  getSignaturesForScope(scope).map(signature => signature.code);

/**
 * Signatures catalog
 */
export const signatures = {
  /**
   * Get signature by code
   */
  getSignatureByCode,

  /**
   * Get the catalog of signatures
   */
  getCatalog: () => signatureCatalog,

  /**
   * Get the signatures that can be used to sign documents
   */
  getDocumentSignatures,

  /**
   * Get the document signe codes of the signatures that can be used to sign documents
   */
  getDocumentSignatureCodes,

  /**
   * Get signatures for a given signatures scope
   */
  getSignaturesForScope,

  /**
   * Get the signatures codes for a given signatures scope
   */
  getSignaturesForScopeCodes,

  /**
   * Check if a signature can be used to sign documents
   */
  isDocumentSignature,

  /**
   * Get the signatures that can be used to sign qualified documents
   */
  getQualifiedSignatures,

  /**
   * Get the qualified signe codes of the signatures that can be used to sign qualified documents
   */
  getQualifiedSignatureCodes,

  /**
   * Check if a signature can be used to sign qualified documents
   */
  isQualifiedSignature,
};
